// Provenance: repo:tds-reconciliation-portal:frontend/src/pages/ReportsPage.tsx
// (ADR-0005) — restyled on the estate tokens (ADR-0010). Ids became UUID strings; runs
// show by their 8-hex tag; every `<a href download>` became an authenticated download
// (DownloadButton) — an anchor cannot carry a Bearer. The grain discipline, the pivots,
// the two statements and the downloads tab carry unchanged.
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { Download, FileBarChart } from 'lucide-react'
import type { ReactNode } from 'react'
import { useState } from 'react'
import { Link, useParams } from 'react-router'

import { api } from '../../api/client'
import {
  useDifference,
  useDifferenceSummary,
  useParties,
  useReportsSummary,
  useRuns,
} from '../../api/hooks'
import { exportLinks, reportInventory } from '../../api/reportLinks'
import type { Grain } from '../../api/reportLinks'
import { CategoryBadge, Chip } from '../../components/badges'
import { ChipStrip, StatChip } from '../../components/ChipStrip'
import { DocumentsSection } from '../../components/DocumentsSection'
import { DownloadButton } from '../../components/DownloadButton'
import { Money } from '../../components/Money'
import { NoRunLoaded } from '../../components/NoRunLoaded'
import { REPORTS_REFRESH_KEYS, RefreshButton } from '../../components/RefreshButton'
import { TablePager } from '../../components/TablePager'
import { runTag } from '../../format'
import { useRunSelection } from '../../workspace/runContext'

const PAGE_SIZE = 50
const fmt = new Intl.NumberFormat('en-IN')

const th = 'px-2 py-1.5 text-left text-xs font-medium uppercase text-muted-foreground'
const td = 'px-2 py-1.5'

/** The difference bucket each category report covers, for the entry grain.
 *  `detailed` filters nothing, so it carries no status. */
const TAB_STATUS: Record<string, string | undefined> = {
  all_match: 'ALL_MATCH',
  in_26as_not_books: 'ONLY_26AS',
  in_books_not_26as: 'ONLY_BOOKS',
  detailed: undefined,
}

const TAB_CATEGORY: Record<string, string | undefined> = {
  all_match: 'Matched - agreed',
  in_26as_not_books: 'In 26AS only (not booked in ledger)',
  in_books_not_26as: 'In ledger only (not in 26AS)',
  detailed: undefined,
}

/** The downloads for one report, at whichever grain is on screen.
 *
 *  These used to live on a separate page, where the same four labels appeared
 *  twice at two grains with nothing telling them apart. The count travels on
 *  the button now, and `exportLinks` picks the endpoint, so the two can no
 *  longer be crossed.
 */
function GrainDownloads({
  runId, tab, grain, onGrain,
}: {
  runId: string
  tab: string
  grain: Grain | null
  onGrain: (g: Grain) => void
}) {
  const summaryQ = useReportsSummary(runId)
  const diffQ = useDifferenceSummary(runId)
  const row = reportInventory(runId, summaryQ.data, diffQ.data).find((r) => r.tab === tab)
  if (!row) return null
  return (
    <div className="flex flex-nowrap items-center gap-3">
      {!row.isPivot && (
        <span className="inline-flex shrink-0 overflow-hidden rounded-md border border-border text-xs">
          {row.grains.map((g) => (
            <button
              key={String(g.grain)}
              type="button"
              className={`px-2 py-1 ${(grain ?? 'deductor') === g.grain ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
              onClick={() => g.grain != null && onGrain(g.grain)}
            >
              {g.count == null ? g.label : `${g.label} · ${fmt.format(g.count)}`}
            </button>
          ))}
        </span>
      )}
      {row.grains
        .filter((g) => row.isPivot || g.grain === (grain ?? 'deductor'))
        .map((g) => (
          <span key={String(g.grain)} className="flex flex-nowrap items-center gap-1.5">
            <span className="whitespace-nowrap text-xs text-muted-foreground">
              {g.count == null ? 'Download' : `${fmt.format(g.count)} rows`}
            </span>
            {g.links.map((l) => (
              <DownloadButton key={l.format} path={l.href}
                label={l.format === 'xlsx' ? 'Excel' : 'CSV'} />
            ))}
          </span>
        ))}
    </div>
  )
}

/** The same four categories, one entry per transaction rather than per
 *  deductor. Same question, different grain — a partner wants the deductors,
 *  a preparer wants the entries behind them. */
function EntryGrainTable({ runId, tab, pagerMiddle }: {
  runId: string
  tab: string
  /** the grain toggle + export buttons, sharing the pager's row (one band) */
  pagerMiddle?: ReactNode
}) {
  const [page, setPage] = useState(1)
  const [size, setSize] = useState(PAGE_SIZE)
  const q = useDifference(runId, { status: TAB_STATUS[tab], page, size })
  const data = q.data
  return (
    <>
      {q.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {data && (
        <>
          <TablePager total={data.total} page={page} size={size}
            onPage={setPage} onSize={setSize} middle={pagerMiddle} />
          <div className="overflow-x-auto rounded-md border border-border">
            <table className="w-full min-w-[900px] text-xs">
              <thead>
                <tr>
                  <th className={th}>Party</th>
                  <th className={th}>Section</th>
                  <th className={th}>26AS date</th>
                  <th className={th}>Books date</th>
                  <th className={`${th} text-right`}>TDS 26AS</th>
                  <th className={`${th} text-right`}>TDS books</th>
                  <th className={`${th} text-right`}>Difference</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((r) => (
                  <tr key={r.id} className="border-t border-border">
                    <td className={td} style={{ maxWidth: 280 }}>
                      <p className="text-sm text-foreground">{r.party}</p>
                    </td>
                    <td className={`${td} text-muted-foreground`}>{r.section ?? '—'}</td>
                    <td className={`${td} font-mono`}>{r.date_26as?.slice(0, 10) ?? '—'}</td>
                    <td className={`${td} font-mono`}>{r.date_books?.slice(0, 10) ?? '—'}</td>
                    <td className={`${td} text-right`}><Money v={r.tds_26as ?? '0'} /></td>
                    <td className={`${td} text-right`}><Money v={r.tds_books ?? '0'} /></td>
                    <td className={`${td} text-right`}><Money v={r.tds_diff ?? '0'} colorize /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {data.total > size && (
            <TablePager total={data.total} page={page} size={size}
              onPage={setPage} onSize={setSize} compact />
          )}
        </>
      )}
    </>
  )
}

function ReportTab({ runId, tab }: { runId: string; tab: string }) {
  const { clientId, periodId } = useParams()
  const [page, setPage] = useState(1)
  const [size, setSize] = useState(PAGE_SIZE)
  const [grain, setGrain] = useState<Grain>('deductor')
  const summaryQ = useReportsSummary(runId)
  const info = summaryQ.data?.tabs[tab]
  const partiesQ = useParties(runId, { category: TAB_CATEGORY[tab], page, size },
                              grain === 'deductor')
  const data = partiesQ.data

  // the grain toggle and export buttons share the pager's row (one band) —
  // the sentence that used to sit here said "Export from the Download tab",
  // the page pointing elsewhere for the button that belongs on it
  const controls = (
    <GrainDownloads runId={runId} tab={tab} grain={grain}
      onGrain={(g) => { setGrain(g); setPage(1) }} />
  )

  return (
    <div className="mt-3 space-y-3">
      {/* the six cards became one slim chip strip */}
      {info && (
        <ChipStrip>
          <StatChip label="Rows" value={info.count} />
          <StatChip label="Amount 26AS" value={info.amt_26as} />
          <StatChip label="TDS 26AS" value={info.tds_26as} />
          <StatChip label="TDS books" value={info.tds_books} />
          <StatChip label="Difference" value={info.diff} />
          {info.within_tolerance > 0 && (
            <StatChip label="Within tolerance" value={info.within_tolerance} />
          )}
        </ChipStrip>
      )}
      {/* the category's recommended next action — one dimmed line, because a
          junior reads this before the numbers mean anything */}
      {info?.action && (
        <p className="text-xs text-muted-foreground">{info.action}</p>
      )}

      {grain === 'transaction' && (
        <EntryGrainTable runId={runId} tab={tab} pagerMiddle={controls} />
      )}

      {grain === 'deductor' && partiesQ.isLoading && (
        <p className="text-sm text-muted-foreground">Loading…</p>
      )}
      {grain === 'deductor' && data && (
        <>
          <TablePager total={data.total} page={page} size={size}
            onPage={setPage} onSize={setSize} middle={controls} />
          <div className="overflow-x-auto rounded-md border border-border">
            <table className="w-full min-w-[900px] text-xs">
              <thead>
                <tr>
                  <th className={th}>Party</th>
                  {tab === 'detailed' && <th className={th}>Category</th>}
                  <th className={`${th} text-right`}>Amount 26AS</th>
                  <th className={`${th} text-right`}>TDS 26AS</th>
                  <th className={`${th} text-right`}>TDS books</th>
                  <th className={`${th} text-right`}>Difference</th>
                  <th className={th}>Entries</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((p) => (
                  <tr key={p.id} className="border-t border-border">
                    <td className={td} style={{ maxWidth: 280 }}>
                      <Link
                        to={`/w/${clientId}/${periodId}/reconciliation/parties/${p.id}?run=${runId}`}
                        className="text-sm font-semibold text-primary hover:underline"
                      >
                        {p.party}
                      </Link>
                    </td>
                    {tab === 'detailed' && (
                      <td className={td}>
                        <CategoryBadge category={p.category}
                          action={summaryQ.data?.category_actions?.[p.category]} />
                      </td>
                    )}
                    <td className={`${td} text-right`}><Money v={p.amt_26as} /></td>
                    <td className={`${td} text-right`}><Money v={p.tds_26as} /></td>
                    <td className={`${td} text-right`}><Money v={p.ledger_dr} /></td>
                    <td className={`${td} text-right`}><Money v={p.diff} colorize /></td>
                    <td className={td}>
                      <span className="text-xs text-muted-foreground">{p.n26} / {p.nled}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {data.total > size && (
            <TablePager total={data.total} page={page} size={size}
              onPage={setPage} onSize={setSize} compact />
          )}
        </>
      )}
    </div>
  )
}

const PIVOTS: [string, string][] = [
  ['party', 'Party-wise'],
  ['section', 'Section-wise'],
  ['month', 'Month-wise'],
  ['quarter', 'Quarter-wise'],
  ['tan', 'TAN-wise'],
]

//: the two STATEMENTS — grainless label/amount walks for readers OUTSIDE the
//: reconciliation: the return preparer (bridge) and the auditor
//: (substantiation).
const STATEMENTS: [string, string][] = [
  ['bridge', 'Claimable TDS'],
  ['substantiation', 'Substantiation'],
]

interface StatementLine {
  key: string
  label: string
  amount: string
  n: number | null
  note?: string | null
}

interface BridgeData {
  lines: StatementLine[]
  claimable: string
  foots: boolean
  pending: { not_claimable: number }
  memo: { ledger_cr: string; gap: string; final: string; at_risk: string }
}

interface SubstantiationData {
  balances: {
    opening: string; closing: string; debit_total: string
    credit_total: string; ties_ok: boolean | null
  } | null
  components: StatementLine[]
  closing_explained: string
  foots: boolean
  note: string | null
}

/** One statement as a dense label/amount walk. The `foots` flag is the
 *  bridge's own footing check — every entry in exactly one line, the lines
 *  summing to the claimable figure — and a statement that does not foot says
 *  so in red rather than printing a confident wrong number. */
function StatementTab({ runId, kind }: { runId: string; kind: string }) {
  const q = useQuery({
    queryKey: ['statement', runId, kind],
    queryFn: () => api<BridgeData | SubstantiationData>(
      `/runs/${runId}/${kind === 'bridge' ? 'bridge' : 'substantiation'}`),
  })
  if (q.isLoading) return <p className="mt-4 text-sm text-muted-foreground">Loading…</p>
  const d = q.data
  if (!d) return null
  const bridge = kind === 'bridge' ? (d as BridgeData) : null
  const subst = kind === 'bridge' ? null : (d as SubstantiationData)
  const rows: StatementLine[] = bridge ? bridge.lines : subst!.components
  const total = bridge ? bridge.claimable : subst!.closing_explained
  const links = exportLinks(runId, 'bridge', null)

  return (
    <div className="mt-3 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <ChipStrip>
          {bridge ? (
            <>
              <StatChip label="TDS credit claimable" value={bridge.claimable} />
              <StatChip label="26AS credit at risk (not F)" value={bridge.memo.at_risk} />
            </>
          ) : (
            <>
              {subst!.balances && (
                <StatChip label="Closing balance" value={subst!.balances.closing} />
              )}
              <StatChip label="Debit movement explained" value={subst!.closing_explained} />
            </>
          )}
        </ChipStrip>
        <span className="flex flex-nowrap items-center gap-1.5">
          {bridge && bridge.pending.not_claimable > 0 && (
            <Chip tone="yellow" solid>
              {bridge.pending.not_claimable} not-claimable awaiting checker
            </Chip>
          )}
          {!d.foots && <Chip tone="red" solid>DOES NOT FOOT — investigate</Chip>}
          {links.map((l) => (
            <DownloadButton key={l.format} path={l.href}
              label={l.format === 'xlsx' ? 'Excel' : 'CSV'} />
          ))}
        </span>
      </div>

      {subst?.balances && (
        <p className="text-xs text-muted-foreground">
          Roll-forward: opening ₹{subst.balances.opening} + Dr ₹
          {subst.balances.debit_total} − Cr ₹{subst.balances.credit_total} =
          closing ₹{subst.balances.closing}
          {subst.balances.ties_ok ? ' — ties' : ' — does not tie'}
        </p>
      )}
      {subst?.note && <p className="text-xs text-orange-800">{subst.note}</p>}

      <div className="overflow-x-auto rounded-md border border-border">
        <table className="w-full min-w-[640px] text-xs">
          <thead>
            <tr>
              <th className={th}>Line</th>
              <th className={`${th} text-right`}>Entries</th>
              <th className={`${th} text-right`}>Amount</th>
              <th className={th}>Note</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((ln) => (
              <tr key={ln.key} className="border-t border-border">
                <td className={td}>{ln.label}</td>
                <td className={`${td} text-right text-muted-foreground`}>{ln.n ?? ''}</td>
                <td className={`${td} text-right`}>
                  <Money v={ln.amount} colorize={Number(ln.amount) < 0} />
                </td>
                <td className={`${td} text-muted-foreground`}>{ln.note ?? ''}</td>
              </tr>
            ))}
            <tr className="border-t border-border font-bold">
              <td className={td}>
                {bridge ? '= TDS credit claimable' : '= Debit movement explained'}
              </td>
              <td />
              <td className={`${td} text-right`}><Money v={total} bold /></td>
              <td className={td}>
                <span className={`text-xs font-semibold ${d.foots ? 'text-emerald-700' : 'text-red-700'}`}>
                  {d.foots ? 'foots' : 'DOES NOT FOOT'}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  )
}

interface PivotRow {
  key: string
  /** the party the row aggregates — set only on the party-wise pivot, so
   *  each row can link to its party page like the category reports do */
  party_id: string | null
  n_26as: number | null
  n_books: number | null
  amount_26as: string | null
  tds_26as: string | null
  tds_books: string | null
  diff: string | null
  category: string
}

interface PivotData {
  by: string
  label: string
  /** one page of rows — `count` and `totals` still describe the whole set */
  rows: PivotRow[]
  totals: { tds_26as: string; tds_books: string; diff: string }
  count: number
  page: number
  size: number
}

/** Difference pivots — party / section / month-wise, each side bucketed by
 *  its OWN date so cut-off drift shows as month asymmetry. */
function PivotTab({ runId, by }: { runId: string; by: string }) {
  const { clientId, periodId } = useParams()
  const [page, setPage] = useState(1)
  const [size, setSize] = useState(PAGE_SIZE)
  const pivotQ = useQuery({
    queryKey: ['pivot', runId, by, page, size],
    queryFn: () => api<PivotData>(`/runs/${runId}/pivots?by=${by}&page=${page}&size=${size}`),
    placeholderData: keepPreviousData,
  })
  const p = pivotQ.data
  if (pivotQ.isLoading) return <p className="mt-4 text-sm text-muted-foreground">Loading…</p>
  if (!p) return null
  return (
    <div className="mt-3 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <ChipStrip>
          <StatChip label="Groups" value={p.count} />
          <StatChip label="TDS 26AS" value={p.totals.tds_26as} />
          {by !== 'section' && <StatChip label="TDS books" value={p.totals.tds_books} />}
          {by !== 'section' && <StatChip label="Difference" value={p.totals.diff} />}
        </ChipStrip>
        {/* the buttons themselves, not "Export from the Download tab" */}
        <GrainDownloads runId={runId} tab={by} grain={null} onGrain={() => {}} />
      </div>
      <div className="overflow-x-auto rounded-md border border-border">
        <table className="w-full min-w-[760px] text-xs">
          <thead>
            <tr>
              <th className={th}>
                {({ month: 'Month', section: 'Section', quarter: 'Quarter',
                    tan: 'TAN' } as Record<string, string>)[by] ?? 'Party'}
              </th>
              <th className={`${th} text-right`}>Entries 26AS</th>
              <th className={`${th} text-right`}>Entries books</th>
              <th className={`${th} text-right`}>Amount 26AS</th>
              <th className={`${th} text-right`}>TDS 26AS</th>
              <th className={`${th} text-right`}>TDS books</th>
              <th className={`${th} text-right`}>Difference</th>
              <th className={th}>Note</th>
            </tr>
          </thead>
          <tbody>
            {p.rows.map((r) => (
              <tr key={r.key} className="border-t border-border odd:bg-muted/30">
                <td className={`${td} font-semibold`} style={{ maxWidth: 260 }}>
                  {r.party_id != null ? (
                    // same link the category reports carry — uniformity: a
                    // party anywhere on this page opens its page
                    <Link
                      to={`/w/${clientId}/${periodId}/reconciliation/parties/${r.party_id}?run=${runId}`}
                      className="text-sm font-semibold text-primary hover:underline"
                    >
                      <span className="block truncate">{r.key}</span>
                    </Link>
                  ) : (
                    <span className="block truncate text-sm">{r.key}</span>
                  )}
                </td>
                <td className={`${td} text-right`}>{r.n_26as ?? ''}</td>
                <td className={`${td} text-right`}>{r.n_books ?? ''}</td>
                <td className={`${td} text-right`}>
                  {r.amount_26as ? <Money v={r.amount_26as} /> : ''}
                </td>
                <td className={`${td} text-right`}>
                  {r.tds_26as ? <Money v={r.tds_26as} /> : ''}
                </td>
                <td className={`${td} text-right`}>
                  {r.tds_books ? <Money v={r.tds_books} /> : ''}
                </td>
                <td className={`${td} text-right`}>
                  {r.diff ? <Money v={r.diff} colorize /> : ''}
                </td>
                <td className={`${td} text-muted-foreground`}>{r.category}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <TablePager total={p.count} page={page} size={size} onPage={setPage} onSize={setSize} />
    </div>
  )
}

/** The party schedule download: pick one party for a single file, or several
 *  for one workbook with a sheet per party. The scope filter narrows what
 *  goes into the file; default is everything — header figures, matched groups
 *  paired, explained entries with their reason, open entries date-ordered. */
function PartySchedule({ runId }: { runId: string }) {
  const q = useQuery({
    queryKey: ['party-names', runId],
    queryFn: () => api<{ items: { party: string }[] }>(
      `/runs/${runId}/parties?size=500`),
  })
  const [picked, setPicked] = useState<string[]>([])
  const [scope, setScope] = useState('all')
  const [filter, setFilter] = useState('')
  const [open, setOpen] = useState(false)
  const names = (q.data?.items ?? []).map((p) => p.party)
  const shown = filter
    ? names.filter((n) => n.toUpperCase().includes(filter.trim().toUpperCase()))
    : names
  const qs = picked.map((p) => `parties=${encodeURIComponent(p)}`).join('&')
  return (
    <div className="mt-3 flex flex-wrap items-center gap-1.5">
      <p className="text-sm font-semibold text-foreground">Party schedule</p>
      <div className="relative">
        <button
          type="button"
          className="w-80 rounded-md border border-border bg-background px-2 py-1 text-left text-xs"
          onClick={() => setOpen((s) => !s)}
        >
          {picked.length
            ? `${picked.length} part${picked.length === 1 ? 'y' : 'ies'} picked`
            : 'Pick one or more parties'}
        </button>
        {open && (
          <div className="absolute left-0 top-full z-30 mt-1 w-96 rounded-md border border-border bg-background p-2 shadow-lg">
            <input
              className="mb-1.5 w-full rounded-md border border-border bg-background px-2 py-1 text-xs outline-none focus:border-primary"
              placeholder="Filter names"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
            <div className="max-h-56 space-y-0.5 overflow-y-auto">
              {shown.slice(0, 200).map((n) => (
                <label key={n} className="flex cursor-pointer items-center gap-1.5 text-xs">
                  <input
                    type="checkbox"
                    checked={picked.includes(n)}
                    onChange={() => setPicked((cur) =>
                      cur.includes(n) ? cur.filter((x) => x !== n) : [...cur, n])}
                  />
                  <span className="truncate">{n}</span>
                </label>
              ))}
              {shown.length === 0 && (
                <p className="p-1 text-xs text-muted-foreground">No party matches that.</p>
              )}
            </div>
            <div className="mt-1.5 flex justify-between">
              {picked.length > 0 && (
                <button type="button" className="text-xs text-primary hover:underline"
                  onClick={() => setPicked([])}>
                  Clear
                </button>
              )}
              <button type="button" className="ml-auto text-xs text-primary hover:underline"
                onClick={() => setOpen(false)}>
                Done
              </button>
            </div>
          </div>
        )}
      </div>
      <select
        className="rounded-md border border-border bg-background px-2 py-1 text-xs outline-none focus:border-primary"
        value={scope}
        onChange={(e) => setScope(e.target.value)}
        aria-label="Schedule scope"
      >
        <option value="all">Everything</option>
        <option value="open">Open only</option>
        <option value="matched">Matched only</option>
        <option value="explained">Explained only</option>
      </select>
      {(['xlsx', 'csv'] as const).map((f) => (
        <DownloadButton
          key={f}
          disabled={!picked.length}
          path={`/runs/${runId}/parties/schedule?${qs}&scope=${scope}&format=${f}`}
          label={f === 'xlsx' ? 'Excel' : 'CSV'}
        />
      ))}
      <p className="text-xs text-muted-foreground">
        full transaction schedule; several parties = one workbook, a sheet each
      </p>
    </div>
  )
}

export function ReportsPage() {
  const { periodId } = useParams()
  const pid = periodId ?? ''
  const runsQ = useRuns(pid)
  const completeRuns = (runsQ.data ?? []).filter((r) => r.status === 'complete')
  // no fallback to completeRuns[0]: a run this session did not load is not
  // this session's to show, however recent it is
  const { runId, setRun } = useRunSelection(pid)
  const summaryQ = useReportsSummary(runId)
  const [top, setTop] = useState('reports')
  const [tab, setTab] = useState('all_match')

  return (
    <div className="space-y-4">
      {/* the page's two small controls open the content area */}
      <div className="flex flex-nowrap items-center gap-2">
        <RefreshButton keys={REPORTS_REFRESH_KEYS} />
        {completeRuns.length > 0 && (
          <select
            className="rounded-md border border-border bg-background px-2 py-1 text-xs outline-none focus:border-primary"
            value={runId ?? ''}
            onChange={(e) => e.target.value && setRun(e.target.value)}
            aria-label="Run"
          >
            {runId == null && <option value="">Run…</option>}
            {completeRuns.map((r) => (
              <option key={r.id} value={r.id}>Run {runTag(r.id)}</option>
            ))}
          </select>
        )}
      </div>

      {runId == null && <NoRunLoaded surface="reports" />}

      {runId != null && (
        <>
          {/* reading reports and downloading documents are DIFFERENT
              activities, so they are sibling top-level tabs. Reports is the
              default — the data is what the page is for. */}
          <div className="flex border-b border-border">
            {[
              { value: 'reports', label: 'Reports', icon: <FileBarChart size={15} /> },
              { value: 'downloads', label: 'Downloads — workbook, memo & source files', icon: <Download size={15} /> },
            ].map((t) => (
              <button
                key={t.value}
                type="button"
                onClick={() => setTop(t.value)}
                className={`flex flex-1 items-center justify-center gap-2 border-b-2 px-3 py-2 text-sm font-medium ${
                  top === t.value
                    ? 'border-primary text-foreground'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                {t.icon} {t.label}
              </button>
            ))}
          </div>

          {top === 'reports' && (
            <div>
              <div className="flex flex-wrap border-b border-border">
                {['all_match', 'in_26as_not_books', 'in_books_not_26as', 'detailed'].map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setTab(t)}
                    className={`flex items-center gap-1.5 border-b-2 px-2.5 py-2 text-xs font-medium ${
                      tab === t
                        ? 'border-primary text-foreground'
                        : 'border-transparent text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {summaryQ.data?.tabs[t]?.label ?? t}
                    {summaryQ.data && (
                      <Chip tone="blue">{fmt.format(summaryQ.data.tabs[t]?.count ?? 0)}</Chip>
                    )}
                  </button>
                ))}
                {PIVOTS.map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setTab(`pivot-${key}`)}
                    className={`border-b-2 px-2.5 py-2 text-xs font-medium ${
                      tab === `pivot-${key}`
                        ? 'border-primary text-foreground'
                        : 'border-transparent text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {label}
                  </button>
                ))}
                {STATEMENTS.map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setTab(`stmt-${key}`)}
                    className={`border-b-2 px-2.5 py-2 text-xs font-medium ${
                      tab === `stmt-${key}`
                        ? 'border-primary text-foreground'
                        : 'border-transparent text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {['all_match', 'in_26as_not_books', 'in_books_not_26as', 'detailed'].includes(tab) && (
                <ReportTab runId={runId} tab={tab} />
              )}
              {tab.startsWith('pivot-') && (
                <PivotTab runId={runId} by={tab.slice('pivot-'.length)} />
              )}
              {tab.startsWith('stmt-') && (
                <StatementTab runId={runId} kind={tab.slice('stmt-'.length)} />
              )}
            </div>
          )}

          {top === 'downloads' && (
            <div className="space-y-3">
              {/* the two ends of the run — what it PRODUCED and what went
                  INTO it */}
              <DocumentsSection runId={runId} periodId={pid} />
              {/* the per-party matched/explained/open statement */}
              <div className="mt-3 flex flex-wrap items-center gap-1.5">
                <p className="text-sm font-semibold text-foreground">Party statement</p>
                <p className="text-xs text-muted-foreground">
                  matched / explained / open, one row per party
                </p>
                {(['xlsx', 'csv'] as const).map((f) => (
                  <DownloadButton key={f} label={f === 'xlsx' ? 'Excel' : 'CSV'}
                    path={`/runs/${runId}/difference/export?report=statement&format=${f}`} />
                ))}
              </div>
              {/* ONE party's schedule on paper — or several, a sheet each */}
              <PartySchedule runId={runId} />
              {/* the register's paper door lives here; the chasing itself
                  lives on each party page */}
              <div className="mt-3 flex flex-wrap items-center gap-1.5">
                <p className="text-sm font-semibold text-foreground">Follow-up register</p>
                <p className="text-xs text-muted-foreground">
                  party, open amounts, owner, status, age — the chase list
                </p>
                {(['xlsx', 'csv'] as const).map((f) => (
                  <DownloadButton key={f} label={f === 'xlsx' ? 'Excel' : 'CSV'}
                    path={`/runs/${runId}/follow-up-register/export?format=${f}`} />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
